import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

type FundingSource = {
  _id?: string;
  title: string;
  category: 'ongoing' | 'completed';
  sponsor?: string;
  period?: string;
  amount?: string;
  description?: string;
  order?: number;
};

const emptyFundingSource: FundingSource = {
  title: '',
  category: 'ongoing',
  sponsor: '',
  period: '',
  amount: '',
  description: '',
  order: 0,
};

type InlineRichTextEditorProps = {
  label: string;
  value: string;
  onChange: (html: string) => void;
};

const InlineRichTextEditor: React.FC<InlineRichTextEditorProps> = ({
  label,
  value,
  onChange,
}) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ script: 'sub' }, { script: 'super' }],
        ['clean'],
      ],
    }),
    []
  );

  const formats = ['bold', 'italic', 'underline', 'script'];

  return (
    <div>
      <label className="mb-1 block font-medium text-neutral-700">{label}</label>
      <div className="rounded-md border border-neutral-300 bg-white">
        <ReactQuill
          className="inline-richtext-editor"
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
        />
      </div>
    </div>
  );
};

const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const AdminFundingSources: React.FC = () => {
  const [items, setItems] = useState<FundingSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<FundingSource | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<'ongoing' | 'completed'>(
    'ongoing'
  );

  const loadItems = async () => {
    try {
      setLoading(true);
      const res = await api.get<FundingSource[]>('/funding-sources');
      setItems(res.data || []);
    } catch (err) {
      console.error(err);
      setError('Failed to load project records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const openCreate = () => {
    setEditing({ ...emptyFundingSource, category: activeCategory });
    setError(null);
  };

  const openEdit = (item: FundingSource) => {
    setEditing({
      ...item,
      sponsor: item.sponsor || '',
      period: item.period || '',
      amount: item.amount || '',
      description: item.description || '',
      order: item.order ?? 0,
    });
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    if (!editing) return;
    const { name, value } = e.target;
    if (name === 'order') {
      const num = value === '' ? 0 : Number(value);
      setEditing({ ...editing, order: Number.isNaN(num) ? 0 : num });
      return;
    }
    setEditing({ ...editing, [name]: value });
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!stripHtml(editing.title)) {
      setError('Please fill in project title.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload: FundingSource = {
        ...editing,
        title: editing.title.trim(),
        order: editing.order ?? 0,
      };

      if (editing._id) {
        await api.put(`/funding-sources/${editing._id}`, payload);
      } else {
        await api.post('/funding-sources', payload);
      }

      setEditing(null);
      await loadItems();
    } catch (err) {
      console.error(err);
      setError('Save failed. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: FundingSource) => {
    if (!item._id) return;
    const ok = window.confirm(`Are you sure you want to delete "${stripHtml(item.title)}"?`);
    if (!ok) return;
    try {
      await api.delete(`/funding-sources/${item._id}`);
      await loadItems();
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-alert
      alert('Delete failed. Please try again later.');
    }
  };

  const visibleItems = useMemo(
    () => items.filter((item) => item.category === activeCategory),
    [items, activeCategory]
  );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Funding Sources Management
        </h1>
        <button
          onClick={openCreate}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700"
        >
          Add Project
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-600">Loading...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-subtle">
          <div className="flex items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-700">
            <span className="mr-1">Category:</span>
            <button
              type="button"
              onClick={() => setActiveCategory('ongoing')}
              className={`rounded-full px-3 py-0.5 ${
                activeCategory === 'ongoing'
                  ? 'bg-brand-red text-white'
                  : 'bg-white text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              On-going Projects
            </button>
            <button
              type="button"
              onClick={() => setActiveCategory('completed')}
              className={`rounded-full px-3 py-0.5 ${
                activeCategory === 'completed'
                  ? 'bg-brand-red text-white'
                  : 'bg-white text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              Completed Projects
            </button>
          </div>

          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr>
                <th className="px-3 py-2 font-semibold text-neutral-700">Title</th>
                <th className="px-3 py-2 font-semibold text-neutral-700">Sponsor</th>
                <th className="px-3 py-2 font-semibold text-neutral-700">Period</th>
                <th className="px-3 py-2 font-semibold text-neutral-700">Amount</th>
                <th className="px-3 py-2 font-semibold text-neutral-700">Order</th>
                <th className="px-3 py-2 font-semibold text-neutral-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item) => (
                <tr key={item._id} className="border-b border-neutral-100">
                  <td className="max-w-xs px-3 py-2 text-neutral-900">
                    <span className="line-clamp-2">{stripHtml(item.title)}</span>
                  </td>
                  <td className="max-w-xs px-3 py-2 text-neutral-700">
                    <span className="line-clamp-2">{stripHtml(item.sponsor || '')}</span>
                  </td>
                  <td className="max-w-xs px-3 py-2 text-neutral-700">
                    <span className="line-clamp-2">{stripHtml(item.period || '')}</span>
                  </td>
                  <td className="max-w-xs px-3 py-2 text-neutral-700">
                    <span className="line-clamp-2">{stripHtml(item.amount || '')}</span>
                  </td>
                  <td className="px-3 py-2 text-neutral-700">{item.order ?? 0}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => openEdit(item)}
                      className="mr-2 text-xs text-brand-red hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="text-xs text-neutral-500 hover:text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {visibleItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-xs text-neutral-500">
                    No records yet. Click Add Project in the top-right corner.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-subtle">
            <h2 className="mb-4 text-sm font-semibold text-neutral-900">
              {editing._id ? 'Edit Project' : 'Add Project'}
            </h2>
            <div className="space-y-3 text-xs">
              <InlineRichTextEditor
                label="Project title"
                value={editing.title}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, title: html } : prev))
                }
              />
              <div>
                <label className="mb-1 block font-medium text-neutral-700">Category</label>
                <select
                  name="category"
                  value={editing.category}
                  onChange={handleChange}
                  className="um-input w-full bg-white px-2 py-1.5"
                >
                  <option value="ongoing">On-going Projects</option>
                  <option value="completed">Completed Projects</option>
                </select>
              </div>
              <InlineRichTextEditor
                label="Sponsor"
                value={editing.sponsor || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, sponsor: html } : prev))
                }
              />
              <InlineRichTextEditor
                label="Period"
                value={editing.period || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, period: html } : prev))
                }
              />
              <InlineRichTextEditor
                label="Amount"
                value={editing.amount || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, amount: html } : prev))
                }
              />
              <InlineRichTextEditor
                label="Description"
                value={editing.description || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, description: html } : prev))
                }
              />
              <div>
                <label className="mb-1 block font-medium text-neutral-700">Sort value</label>
                <input
                  name="order"
                  type="number"
                  value={editing.order ?? 0}
                  onChange={handleChange}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
            </div>
            {error && <p className="mt-2 text-[11px] text-red-600">{error}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="rounded-full border border-neutral-300 px-4 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-brand-red px-4 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFundingSources;
